import { axios, config } from 'data';
import { event, storage } from 'helpers';
import { Nullable } from 'types/common';
import { ProfileStatus } from 'types/models';

import {
  AuthCredentials,
  AuthEndpoint,
  AuthEvent,
  AuthLoginParams,
  AuthLoginResponse,
  AuthPasswordResetConfirmParams,
  AuthPasswordResetParams,
  AuthRefreshAccessTokenParams,
  AuthRegistrationConfirmParams,
  AuthRegistrationParams,
  AuthRegistrationVerifyParams,
} from 'types/services';

const listener = event.createListener();

const getCredentials = () => {
  const remember = Boolean(storage.local.get<boolean>(config.AUTH_REMEMBER_STORAGE_KEY));

  return remember
    ? storage.local.get<AuthCredentials>(config.AUTH_CREDENTIALS_STORAGE_KEY)
    : storage.session.get<AuthCredentials>(config.AUTH_CREDENTIALS_STORAGE_KEY);
};

const setCredentials = (credentials: Nullable<Partial<AuthCredentials>>) => {
  const previousCredentials = getCredentials();
  const newCredentials = credentials && { ...previousCredentials, ...credentials };
  const remember = Boolean(newCredentials?.remember ?? previousCredentials?.remember);

  storage.local.set(config.AUTH_REMEMBER_STORAGE_KEY, remember);

  remember
    ? storage.local.set(config.AUTH_CREDENTIALS_STORAGE_KEY, newCredentials)
    : storage.session.set(config.AUTH_CREDENTIALS_STORAGE_KEY, newCredentials);

  listener.trigger(AuthEvent.CREDENTIALS);
};

const getAccessToken = () => {
  const credentials = getCredentials();

  return credentials && credentials.accessToken;
};

const getRefreshToken = () => {
  const credentials = getCredentials();

  return credentials && credentials.refreshToken;
};

const getStatus = () => {
  const credentials = getCredentials();

  return credentials && credentials.status;
};

const setStatus = (status: ProfileStatus) => {
  setCredentials({ status });
};

const login = async (params: AuthLoginParams) => {
  const response = await axios.post<AuthLoginResponse>(AuthEndpoint.LOGIN, params);

  setCredentials({ ...response.data, remember: params.remember });
};

const logout = async () => {
  try {
    await axios.post<never>(AuthEndpoint.LOGOUT);
  } catch (error) {
    // ignore
  } finally {
    setCredentials(null);
  }
};

const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('Refresh token not found');
  }

  const params: AuthRefreshAccessTokenParams = { refreshToken };
  const response = await axios.post<AuthLoginResponse>(AuthEndpoint.REFRESH_ACCESS_TOKEN, params);

  setCredentials(response.data);
};

const register = async (params: AuthRegistrationParams) => {
  await axios.post<never>(AuthEndpoint.REGISTER, params);
};

const registerVerify = async (params: AuthRegistrationVerifyParams) => {
  await axios.post<never>(AuthEndpoint.REGISTER_VERIFY, params);
};

const registerConfirm = async (params: AuthRegistrationConfirmParams) => {
  await axios.post<never>(AuthEndpoint.REGISTER_CONFIRM, params);
};

const resetPassword = async (params: AuthPasswordResetParams) => {
  await axios.post<never>(AuthEndpoint.PASSWORD_RESET, params);
};

const resetPasswordConfirm = async (params: AuthPasswordResetConfirmParams) => {
  await axios.post<never>(AuthEndpoint.PASSWORD_RESET_CONFIRM, params);
};

const authService = {
  on: listener.on,
  off: listener.off,
  getAccessToken,
  getStatus,
  setStatus,
  login,
  logout,
  refreshAccessToken,
  register,
  registerVerify,
  registerConfirm,
  resetPassword,
  resetPasswordConfirm,
};

export default authService;
